import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation, useNavigate } from "react-router-dom";
import type { AdminApi } from "../api/client.js";
import type {
  AdminProvider,
  AdminProviderInput,
  ProviderType,
} from "../api/types.js";
import { Card } from "../components/Card.js";
import { Field } from "../components/Field.js";
import { Select } from "../components/Select.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { Badge } from "../components/Badge.js";
import { StatesMultiSelect } from "../components/StatesMultiSelect.js";
import {
  GoogleConnectionPanel,
  BusyCalendarsPanel,
} from "../components/GooglePanels.js";

type Props = { api: AdminApi };

const TYPE_OPTIONS: ReadonlyArray<{ value: ProviderType; label: string }> = [
  { value: "virtual_only", label: "Virtual only" },
  { value: "in_person_and_virtual", label: "In-person + virtual" },
];

const TYPE_LABELS: Record<ProviderType, string> = {
  virtual_only: "Virtual only",
  in_person_and_virtual: "In-person + virtual",
};

const ProviderFormSchema = z.object({
  display_name: z.string().min(1, "Required"),
  type: z.enum(["virtual_only", "in_person_and_virtual"]),
  licensed_states: z.array(z.string()),
  max_concurrent: z.coerce.number().int().min(1, "Must be at least 1"),
  active: z.boolean(),
});

type FormValues = z.infer<typeof ProviderFormSchema>;

function emptyForm(): FormValues {
  return {
    display_name: "",
    type: "virtual_only",
    licensed_states: [],
    max_concurrent: 1,
    active: true,
  };
}

function toFormValues(p: AdminProvider): FormValues {
  return {
    display_name: p.display_name,
    type: p.type,
    licensed_states: p.licensed_states,
    max_concurrent: p.max_concurrent,
    active: p.active,
  };
}

function toInput(v: FormValues): AdminProviderInput {
  return {
    display_name: v.display_name,
    type: v.type,
    licensed_states: v.licensed_states,
    max_concurrent: v.max_concurrent,
    active: v.active,
  };
}

// The Google OAuth callback (apps/api/src/routes/admin/google-oauth.ts)
// still hard-redirects to `/settings/calendar?google=connected` /
// `?google=error` regardless of whether the connect was initiated from a
// provider's panel here or the legacy practice-wide panel on
// CalendarConfigPage — that redirect target isn't provider-aware yet.
// Reading `?google=` here too means this page shows the right toast on the
// day the callback is made provider-aware (a later task), without any
// change on this end; until then this branch simply won't fire for
// provider-initiated connects, which land on /settings/calendar instead.
export function ProvidersPage({ api }: Props) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const flash = params.get("google");
  const flashReason = params.get("reason");

  useEffect(() => {
    if (flash === "connected") {
      // Prefix match: hits every provider's google-status query, not just
      // one. Also refetches the providers list (which carries each
      // provider's google_connected/_by_email summary) — busy-calendars
      // queries are left alone; each panel refetches its own once its
      // status flips to connected.
      void queryClient.invalidateQueries({
        queryKey: ["admin", "provider-google-status"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["admin", "providers"],
      });
      navigate(location.pathname, { replace: true });
    }
  }, [flash, queryClient, navigate, location.pathname]);

  const q = useQuery({
    queryKey: ["admin", "providers"],
    queryFn: () => api.listProviders(),
  });
  const [editing, setEditing] = useState<AdminProvider | "new" | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(ProviderFormSchema),
    defaultValues: emptyForm(),
  });

  const licensedStates = watch("licensed_states");

  function openNew() {
    reset(emptyForm());
    setEditing("new");
  }

  function openEdit(p: AdminProvider) {
    reset(toFormValues(p));
    setEditing(p);
  }

  function closeForm() {
    setEditing(null);
  }

  const create = useMutation({
    mutationFn: (input: AdminProviderInput) => api.createProvider(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "providers"] });
      setEditing(null);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: AdminProviderInput }) =>
      api.updateProvider(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "providers"] });
      setEditing(null);
    },
  });

  const submitting = create.isPending || update.isPending;
  const mutationError = create.isError
    ? (create.error as Error).message
    : update.isError
      ? (update.error as Error).message
      : null;

  function onSubmit(v: FormValues) {
    const input = toInput(v);
    if (editing && editing !== "new") {
      update.mutate({ id: editing.id, input });
    } else {
      create.mutate(input);
    }
  }

  const providers = q.data?.providers ?? [];

  return (
    <div className="grid gap-4">
      {flash === "connected" ? (
        <p className="rounded-md bg-emerald-50 p-2 text-sm text-emerald-800">
          Google Calendar connected. Pick which calendars to use in the
          provider's panel below.
        </p>
      ) : null}
      {flash === "error" ? (
        <ErrorBanner
          message={`Google connection failed${flashReason ? `: ${flashReason}` : ""}.`}
        />
      ) : null}

      <Card
        title="Providers"
        action={
          <button
            type="button"
            onClick={openNew}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            New provider
          </button>
        }
      >
        {q.isPending ? (
          <p className="text-sm text-slate-600">Loading…</p>
        ) : null}
        {q.isError ? (
          <ErrorBanner message="Could not load providers." />
        ) : null}
      </Card>

      {q.data ? (
        <div className="grid gap-4">
          {providers.map((p) => (
            <Card
              key={p.id}
              title={p.display_name}
              action={
                <button
                  type="button"
                  onClick={() => openEdit(p)}
                  className="rounded-md border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                >
                  Edit
                </button>
              }
            >
              <div className="grid gap-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="blue">{TYPE_LABELS[p.type]}</Badge>
                  <Badge tone={p.active ? "emerald" : "slate"}>
                    {p.active ? "Active" : "Inactive"}
                  </Badge>
                  <span className="text-slate-600">
                    Max concurrent: {p.max_concurrent}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-slate-600">Licensed in:</span>
                  {p.licensed_states.length === 0 ? (
                    <span className="text-slate-500">—</span>
                  ) : (
                    p.licensed_states.map((code) => (
                      <Badge key={code} tone="slate">
                        {code}
                      </Badge>
                    ))
                  )}
                </div>
                <p className="text-slate-600">
                  Google:{" "}
                  {p.google_connected ? (
                    <span className="font-medium text-slate-900">
                      Connected as {p.google_connected_by_email}
                    </span>
                  ) : (
                    <span className="font-medium text-slate-900">
                      Not connected
                    </span>
                  )}
                </p>
              </div>

              <div className="mt-4 grid gap-4">
                <GoogleConnectionPanel api={api} providerId={p.id} />
                <BusyCalendarsPanel api={api} providerId={p.id} />
              </div>
            </Card>
          ))}
        </div>
      ) : null}

      {editing ? (
        <Card
          title={
            editing === "new"
              ? "New provider"
              : `Edit ${editing.display_name}`
          }
        >
          {mutationError ? <ErrorBanner message={mutationError} /> : null}
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="grid gap-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Display name"
                required
                {...register("display_name")}
                error={errors.display_name?.message}
              />
              <Select
                label="Type"
                required
                options={TYPE_OPTIONS}
                {...register("type")}
                error={errors.type?.message}
              />
            </div>
            <StatesMultiSelect
              label="Licensed states"
              value={licensedStates}
              onChange={(next) =>
                setValue("licensed_states", next, { shouldValidate: true })
              }
              error={errors.licensed_states?.message}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Max concurrent"
                type="number"
                min={1}
                required
                {...register("max_concurrent")}
                error={errors.max_concurrent?.message}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                {...register("active")}
                className="h-4 w-4 rounded border-slate-300 text-blue-600"
              />
              Active
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeForm}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {submitting ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}
